<template>
  <section class="select-products-block">
    <div ref="sectionProducts"/>

    <div class="container">
      <div class="select-products-block__head">
        <h2
          v-if="values.title"
          v-once
          class="select-products-block__title"
          v-html="values.title"
        />
        <NuxtLink v-if="values?.link" :to="values.link">
          {{ values?.link_title || '' }}
          <img
            src="~/assets/img/svg/common/arrow.svg"
            alt="стрелка"
            loading="lazy"
            width="14"
            height="14"
          >
        </NuxtLink>
      </div>
      <div
        v-if="descriptionFromValues"
        v-html="descriptionFromValues"
        class="select-product-block__description"
      />

      <div v-if="!pending && products.length > 0">
        <div
          v-if="$viewport.isGreaterOrEquals('lg')"
          class="select-products-block__body"
          :class="{
            'products': !isProductsCollection,
            'collection': isProductsCollection,
          }"
        >
          <div
            v-for="(product) in products"
            :key="`product-${product.id}`"
          >
            <component
              :is="product.characteristicGroupId === 19 ? CollectionCard : ProductCard"
              :product="product"
            />
          </div>
        </div>
        <div v-else class="select-products-block__mobile-swiper" :style="{ minHeight: isProductsCollection ? '480px' : '480' }">
          <MobileSwiper
            :key="`MobileSwiper-${keyMobileSwiper}`"
            :products="products"
          />
        </div>
      </div>
      <template v-if="pending">
        <div
          v-if="$viewport.isGreaterOrEquals('lg')"
          class="select-products-block__skeleton"
        >
          <div
            v-for="index in (isProductsCollection ? 4 : 3)"
            :key="`skeleton-${index}`"
            class="skeleton-row"
            :style="{ height: isProductsCollection ? '524px' : '524px' }"
          />
        </div>
        <MobileSwiperSkeleton
          v-if="pending && $viewport.isLessThan('lg')"
          :is-collection="isProductsCollection"
          :skeleton-count="3"
        />
      </template>

      <NuxtLink
        v-if="values.link_title && values.link"
        class="btn btn-outline select-products__btn"
        :to="values.link"
      >
        {{ values.link_title }}
      </NuxtLink>
    </div>
  </section>
</template>

<script lang="ts" setup>
import type { IBlockDefaultProps } from '#sitis/internal/models/common';
import type { IProduct } from '#sitis/internal/controllers/products/models/Product';
import { Product } from '#sitis/internal/controllers/products/models/Product';
import { useNuxtApp, computed, ref, defineAsyncComponent, onBeforeUnmount } from '#imports';
import generateId from '~/utils/generateId';
import useEcommerce from "~/composables/useEcommerceYandex";
import { useIntersectionObserver } from "@vueuse/core";
import ProductCard from '~/components/catalog/ProductCard.vue';
import CollectionCard from '~/components/catalog/CollectionCard.vue';
import MobileSwiper from "~/components/select-products-block/MobileSwiper.vue";
import MobileSwiperSkeleton from "~/components/select-products-block/MobileSwiperSkeleton.vue";

// Ленивая загрузка только для мобильного свайпера (не критично для SSR)
// const MobileSwiper = defineAsyncComponent(() => import("~/components/select-products-block/MobileSwiper.vue"));
// const MobileSwiperSkeleton = defineAsyncComponent(() => import("~/components/select-products-block/MobileSwiperSkeleton.vue"));

const props = defineProps<IBlockDefaultProps>();
const {
  $api,
  $viewport
} = useNuxtApp();

const { addEcommerceProductsList } = useEcommerce();

// SSR загрузка данных для корректной работы ссылок и SEO
const products = ref<IProduct[]>([]);
const pending = ref(true);
const getProducts = async () => {
  const apiUrl = (props?.extras?.products_endpoint || '').replace('/api', '');
  if (!apiUrl) {
    pending.value = false;
    return;
  }
  try {
    const res = await $api.agent.get(apiUrl);
    const data = res._data || [];
    products.value = data.map((product: any) => new Product(product));
  } catch (error) {
    console.error('Failed to load products:', error);
    products.value = [];
  } finally {
    pending.value = false;
  }
};

const isProductsCollection = computed(() => {
  if (String(props?.values?.title || '').includes('коллекции')) {
    return true
  }
  return Boolean(products.value?.[0]?.characteristicGroupId === 19)
});

const descriptionFromValues = computed(() => {
  if ($viewport.isLessThan('lg')) {
    return props.values.description_mobile;
  }
  return props.values.description_desktop;
});

const keyMobileSwiper = ref(String(generateId(3)));

const isSendingProducts = ref(false);
const sectionProducts = ref<HTMLDivElement | null>();

const sendingProductsTo = () => {
  if (isSendingProducts.value || !products.value?.length) {
    return;
  }
  isSendingProducts.value = true;
  addEcommerceProductsList(products.value, `${props?.values?.title || ''}`);
};

const { stop } = useIntersectionObserver(
  sectionProducts,
  async ([{ isIntersecting }]) => {
    if (isIntersecting && !isSendingProducts.value) {
      await getProducts();
      sendingProductsTo();
      stop(); // Отписываемся после первой отправки
    }
  },
  {
    threshold: 0.1
  }
);

onBeforeUnmount(() => {
  stop();
});
</script>

<style lang="scss" scoped>
.select-products-block {
	padding: 82px 0;
	box-sizing: border-box;
	background-color: white;
	position: relative;
	z-index: 1;
	border-radius: 20px 20px 0 0;
}

.select-products__btn {
	display: none;
}

.select-products-block__head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 32px;

	& > a {
		display: flex;
		font-size: 16px;
		font-weight: 500;
		line-height: 24px;
		align-items: center;
		color: var(--black);
		gap: 6px;

		&:hover {
			opacity: .7;
		}
	}

	& img {
		transform: rotate(180deg);
		width: 14px;
		height: 14px;
	}
}

.select-product-block__description {
  font-weight: 500;
  line-height: 130%;
  padding-bottom: 32px;
}

.select-products-block__skeleton {
	display: flex;
	flex-direction: column;
	gap: 32px;
}

.skeleton-row {
	width: 100%;
	background: linear-gradient(
		90deg,
		#f0f0f0 0%,
		#e0e0e0 20%,
		#f0f0f0 40%,
		#f0f0f0 100%
	);
	background-size: 200% 100%;
	animation: skeleton-loading 1.5s ease-in-out infinite;
	border-radius: 12px;
}

@keyframes skeleton-loading {
	0% {
		background-position: 200% 0;
	}
	100% {
		background-position: -200% 0;
	}
}

.select-products-block__body {
	display: flex;
	flex-wrap: wrap;
	margin-top: -32px;
	margin-left: -32px;

	& > * {
		margin-top: 32px;
		margin-left: 32px;
	}

	&.collection {
		& > * {
			width: calc(100% / 3 - 32px);
		}
	}

	&.products {
		& > * {
			width: calc(100% / 4 - 32px);
		}
	}
}

@media (max-width: 1699px) {
	.select-products-block__body.collection {
		& > * {
			width: calc(50% - 32px);
		}
	}
	.select-products-block__body.products {
		& > * {
			width: calc(100% / 3 - 32px);
		}
	}
}

@media (max-width: 1359px) {
  .select-products-block__body.products {
    & > * {
      width: calc(100% / 2 - 32px);
    }
  }
}

@media (max-width: 859px) {
  .select-products-block {
    top: -20px;
    padding: 40px 0;
    border-radius: 24px 24px 0 0;
  }
}

@media (max-width: 640px) {
	.select-products-block__head {
		& > a {
			display: none;
		}
	}
	.select-products__btn {
		padding: 15px 24px;
		margin-top: 32px;
		display: flex;
		border-color: var(--black);
		font-weight: 600;
	}
}
</style>
